import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Menu as BaseMenu,
  MenuItem,
  MenuProps,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import { ReactComponent as SearchIcon } from '../../assets/search.svg';
import { ReactComponent as MoreIcon } from '../../assets/more.svg';
import Checkbox from '../Checkbox';
import { Flex, FlexColumn } from '../Flex';
import TextField from '../TextField';
import IconButton from '../IconButton';
import SvgIcon from '../SvgIcon';
import { EMPTY_PLACEHOLDER } from '../../constants';

const MenuItemStyled = styled(MenuItem)<{
  $multiple?: boolean;
  $tall?: boolean;
  $hasDescription?: boolean;
  $highlightSelected?: boolean;
  $bold?: boolean;
  $color?: string;
}>`
  min-height: ${({ $tall, $hasDescription }) =>
    $tall || $hasDescription ? '50px' : '36px'};
  max-height: ${({ $tall, $hasDescription }) =>
    $tall || $hasDescription ? '50px' : '36px'};
  padding: ${({ $multiple, $hasDescription }) =>
    $hasDescription ? '6px 16px' : $multiple ? '8px' : '16px'};
  border-radius: 8px;

  :hover {
    background: ${({ theme }) => theme.palette.common.gray9};
  }

  &.Mui-selected {
    background: ${({ $highlightSelected, theme }) =>
      $highlightSelected ? theme.palette.common.gray6 : 'none'};

    :hover {
      background: ${({ $highlightSelected, theme }) =>
        $highlightSelected
          ? theme.palette.common.gray6
          : theme.palette.common.gray9};
    }
  }

  .MuiTypography-root {
    font-weight: ${({ $bold, theme }) =>
      $bold ? theme.typography.fontWeightBold : 400};
    ${({ $color }) => $color && `color: ${$color};`}
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const MenuTitle = styled(Typography)`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray11};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  padding: 8px 16px;
  white-space: nowrap;
`;

const OptionDescription = styled(Typography)`
  font-size: 12px;
  line-height: normal;
  color: ${({ theme }) => theme.palette.common.gray4};
`;

const ClearMenuItem = styled(MenuItemStyled)`
  margin: 0 4px 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.common.blue};
`;

const OptionsContainer = styled(FlexColumn)<{ $maxHeight?: string }>`
  max-height: ${({ $maxHeight }) => $maxHeight ?? '210px'};
  overflow: auto;
  gap: 8px;
  margin: 0 4px;
`;

export type Action = {
  value: string;
  label: string;
};

export type Option = {
  value: string;
  label: string;
  description?: string;
  color?: string;
  icon?: JSX.Element;
  disabled?: boolean;
  divider?: boolean;
  tooltip?: ReactNode;
  actions?: Action[];
  // Non-selectable section header rendered as a title row (groups options).
  header?: boolean;
};

type Props = {
  menuButton: ReactElement;
  menuOpenedButton?: ReactElement;
  menuSelectedButton?: ReactElement;
  options?: Option[];
  selectedOptions?: string[];
  showSearch?: boolean;
  showClear?: boolean;
  tooltip?: ReactNode;
  closeOnChange?: boolean;
  onOpen?: () => void;
  optionRenderer?: (value: string) => string;
  tall?: boolean;
  highlightSelected?: boolean;
  boldOptions?: boolean;
  menuTitle?: string;
  onAction?: (optionValue: string, actionValue: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  optionsMaxHeight?: string;
};

type MultipleProps =
  | {
      multiple: true;
      onChange: (items: string[], clickedOption?: string) => void;
    }
  | {
      multiple?: false;
      onChange: (items: string) => void;
    };

type CombinedProps = Omit<MenuProps, 'onChange' | 'open'> &
  Props &
  MultipleProps;

const Menu = ({
  menuButton,
  menuOpenedButton,
  menuSelectedButton,
  options = [],
  selectedOptions = [],
  onChange,
  multiple,
  showSearch,
  showClear,
  tooltip,
  closeOnChange,
  onOpen,
  optionRenderer,
  tall,
  highlightSelected,
  boldOptions,
  menuTitle,
  onAction,
  onLoadMore,
  hasMore,
  isLoadingMore,
  optionsMaxHeight,
  ...props
}: CombinedProps) => {
  const [menuAnchor, setMenuAnchor] = useState<
    (EventTarget & HTMLElement) | null
  >(null);
  const [search, setSearch] = useState<string>('');
  const [actionsAnchor, setActionsAnchor] = useState<{
    element: HTMLElement;
    option: Option;
  } | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !onLoadMore || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isLoadingMore]);

  const handleChange = (o: Option) => {
    if (multiple) {
      if (selectedOptions.includes(o.value)) {
        onChange(
          selectedOptions.filter((item) => item !== o.value),
          o.value,
        );
      } else {
        onChange([...selectedOptions, o.value], o.value);
      }
    } else {
      onChange(o.value);
    }

    if (closeOnChange) {
      setMenuAnchor(null);
    }
  };

  const filteredOptions = useMemo(() => {
    const filtered =
      search && search.trim() !== ''
        ? options.filter((o) => o.label.includes(search))
        : options;
    const emptyOptions = filtered.filter((o) => o.value.trim() === '');
    const nonEmptyOptions = filtered.filter((o) => o.value.trim() !== '');
    return [...emptyOptions, ...nonEmptyOptions];
  }, [options, search]);

  const handleOpen = useCallback(
    (target: any) => {
      setMenuAnchor(target);
      onOpen?.();
    },
    [onOpen],
  );

  const getOptionLabel = useCallback(
    (label: string) => {
      if (optionRenderer) {
        return optionRenderer(label);
      }
      return label.trim() !== '' ? label : EMPTY_PLACEHOLDER;
    },
    [optionRenderer],
  );

  const menuButtonFixed = useMemo(
    () =>
      React.cloneElement(
        selectedOptions?.length
          ? menuSelectedButton || menuButton
          : menuAnchor
          ? menuOpenedButton || menuButton
          : menuButton,
      ),
    [
      menuAnchor,
      menuButton,
      menuOpenedButton,
      menuSelectedButton,
      selectedOptions,
    ],
  );

  return (
    <>
      <Tooltip
        sx={{ cursor: 'pointer' }}
        title={tooltip}
        open={tooltip ? undefined : false}
      >
        <div>
          {React.cloneElement(menuButtonFixed, {
            onClick: (e: any) => {
              handleOpen(e.currentTarget);
              e.stopPropagation();
            },
          })}
        </div>
      </Tooltip>

      <BaseMenu
        {...props}
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={(e, reason) => {
          setMenuAnchor(null);
          props.onClose?.(e, reason);
        }}
        onClick={(e: any) => e.stopPropagation()}
      >
        {!!menuTitle && <MenuTitle>{menuTitle}</MenuTitle>}
        {options.length > 5 && showSearch && (
          <TextField
            fullWidth
            placeholder="Search"
            variant="outlined"
            onChange={(event) => setSearch(event.target.value)}
            value={search}
            sx={{ pl: 1, pr: 1, pb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
        {showClear && (
          <ClearMenuItem
            onClick={() => {
              // @ts-ignore
              onChange(multiple ? [] : '');
            }}
          >
            Clear
          </ClearMenuItem>
        )}
        <OptionsContainer $maxHeight={optionsMaxHeight}>
          {filteredOptions.map((o) =>
            o.header ? (
              <MenuTitle key={o.value}>{o.label}</MenuTitle>
            ) : (
              <React.Fragment key={o.value}>
                <Tooltip title={o.tooltip} placement="left">
                  <div>
                    <MenuItemStyled
                      key={o.value}
                      aria-label={o.label.trim() ?? 'N/A'}
                      disabled={o.disabled}
                      onClick={
                        multiple
                          ? undefined
                          : (e) => {
                              handleChange(o);
                              e.stopPropagation();
                            }
                      }
                      selected={
                        highlightSelected &&
                        !multiple &&
                        selectedOptions.includes(o.value)
                      }
                      $tall={tall}
                      $hasDescription={!!o.description}
                      $highlightSelected={highlightSelected}
                      $bold={boldOptions}
                      $color={o.color}
                    >
                      <Flex
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <Flex sx={{ alignItems: 'center', flex: 1 }}>
                          {!multiple && o.icon && (
                            <Flex sx={{ mr: 1.5 }}>
                              {React.cloneElement(o.icon)}
                            </Flex>
                          )}
                          {multiple ? (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedOptions.includes(o.value)}
                                  onClick={() => handleChange(o)}
                                />
                              }
                              label={
                                o.icon ? (
                                  <Flex sx={{ alignItems: 'center', gap: 1 }}>
                                    {React.cloneElement(o.icon)}
                                    <Typography>
                                      {getOptionLabel(o.label)}
                                    </Typography>
                                  </Flex>
                                ) : (
                                  getOptionLabel(o.label)
                                )
                              }
                            />
                          ) : o.description ? (
                            <FlexColumn sx={{ gap: 0.2 }}>
                              <Typography>{getOptionLabel(o.label)}</Typography>
                              <OptionDescription>
                                {o.description}
                              </OptionDescription>
                            </FlexColumn>
                          ) : (
                            <Typography>{getOptionLabel(o.label)}</Typography>
                          )}
                        </Flex>
                        {o.actions && o.actions.length > 0 && (
                          <IconButton
                            sx={{ ml: 1, p: '5px 8px !important' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setActionsAnchor({
                                element: e.currentTarget as HTMLElement,
                                option: o,
                              });
                            }}
                          >
                            <SvgIcon width={14}>
                              <MoreIcon />
                            </SvgIcon>
                          </IconButton>
                        )}
                      </Flex>
                    </MenuItemStyled>
                  </div>
                </Tooltip>
                {o.divider && <Divider light sx={{ m: '0 !important' }} />}
              </React.Fragment>
            ),
          )}
          {hasMore && (
            <Flex
              ref={sentinelRef}
              sx={{ justifyContent: 'center', py: 1, minHeight: 24 }}
            >
              {isLoadingMore && <CircularProgress size={16} />}
            </Flex>
          )}
        </OptionsContainer>
      </BaseMenu>

      <BaseMenu
        anchorEl={actionsAnchor?.element}
        open={!!actionsAnchor}
        onClose={() => setActionsAnchor(null)}
        onClick={(e: any) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <FlexColumn sx={{ gap: 1, mx: 0.5, my: 0.5 }}>
          {actionsAnchor?.option.actions?.map((action) => (
            <MenuItemStyled
              key={action.value}
              onClick={(e) => {
                e.stopPropagation();
                if (onAction && actionsAnchor?.option) {
                  onAction(actionsAnchor.option.value, action.value);
                }
                setActionsAnchor(null);
                setMenuAnchor(null);
              }}
            >
              <Typography>{action.label}</Typography>
            </MenuItemStyled>
          ))}
        </FlexColumn>
      </BaseMenu>
    </>
  );
};

export default Menu;
